import dayjs from "dayjs";
import { returnPriceFormatDE, returPriceTrendFormatDE } from "~/mixins/mixins";

interface PriceData {
  value: number;
  modifiedAt?: number;
}

interface TableRowItem {
  number?: number;
  text: string;
  href: string;
}

export interface TableItem {
  name: string;
  currentPrice?: PriceData;
  lastPrice?: PriceData;
  link?: string;
  zipcode?: string;
  federalStateName?: string;
  seoName?: string;
  previousPrice?: string;
  price?: {
    actual: number;
    previous: number;
  };
  current?: number;
  previous?: number;
  yesterday?: number;
}

export interface TableRowOptions {
  sortKey?: string;
  hrefTemplate: (item: TableItem) => string;
  includeNumber?: boolean;
  differenceCalculation?: (item: TableItem) => number;
  formatName?: (item: TableItem) => string;
}
export function generateTableHeaderData(
  mainColumnTitle: string,
  timestamps: number[] = [],
): string[] {
  const formattedDates = timestamps.map((timestamp) =>
    dayjs.unix(timestamp).format("DD.MM"),
  );

  if (formattedDates.length === 0) {
    const today = dayjs();
    const yesterday = today.subtract(1, "day");

    formattedDates.push(today.format("DD.MM"));
    formattedDates.push(yesterday.format("DD.MM"));
  }

  return [
    mainColumnTitle,
    `Preis am ${formattedDates[0]}`,
    `Preis am ${formattedDates[1]}`,
    "Differenz/Trend",
  ];
}

/**
 * Returns formatted table header data for states
 */
export function returnStateTableHeaderData(state: TableItem[]): string[] {
  return generateTableHeaderData("Bundesland", [
    state[0]?.currentPrice?.modifiedAt ?? null,
    state[0]?.lastPrice?.modifiedAt ?? null,
  ]);
}

/**
 * Formats table row data with sorting and formatting options
 */
export function formatTableRowData(
  items: TableItem[],
  options: TableRowOptions = { hrefTemplate: (item) => "" },
): Array<[TableRowItem | string, string, string, string]> {
  const {
    sortKey = "currentPrice.value",
    hrefTemplate,
    includeNumber = true,
    differenceCalculation = (item: any) => item.current - item.previous,
    formatName = (item: TableItem) => item.name, // Add default implementation
  } = options;

  const sortPath = sortKey.split(".");
  const getByPath = (obj: any): number => {
    let value: any = obj;
    for (let i = 0; i < sortPath.length; i++) {
      if (value == null) return 0;
      value = value[sortPath[i]];
    }
    return (value as number) ?? 0;
  };

  const sortedItems = [...items].sort((a, b) => getByPath(a) - getByPath(b));

  return sortedItems.map((item, index) => {
    const currentPrice = getByPath(item);
    const previousPrice =
      item.previousPrice ??
      item.lastPrice?.value ??
      item.price?.previous ??
      item.yesterday ??
      item.previous ??
      0;
    const difference = differenceCalculation(item);
    const displayName = formatName(item); // Use the formatName function

    const shouldShowPlaceholder = currentPrice === 0 || previousPrice === 0;

    return [
      includeNumber
        ? { number: index + 1, text: displayName, href: hrefTemplate(item) }
        : { text: displayName, href: hrefTemplate(item) },
      currentPrice === 0 ? "--,-- €" : returnPriceFormatDE(currentPrice),
      previousPrice === 0 ? "--,-- €" : returnPriceFormatDE(previousPrice),
      shouldShowPlaceholder ? "--,-- €" : returPriceTrendFormatDE(difference),
    ];
  });
}
